import React, { useState, useEffect } from 'react';
import { X, ExternalLink, Loader } from 'lucide-react';

const PDFViewerModal = ({ isOpen, onClose, pdfUrl, filename, token }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && pdfUrl && token) {
      setLoading(true);
      setError(null);
      setBlobUrl(null);

      fetch(pdfUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(response => {
          if (!response.ok) throw new Error(`Failed to load PDF: ${response.status}`);
          return response.blob();
        })
        .then(blob => {
          setBlobUrl(URL.createObjectURL(blob));
          setLoading(false);
        })
        .catch(err => {
          setError(err.message);
          setLoading(false);
        });
    }
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [isOpen, pdfUrl, token]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col mx-4">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate max-w-md">
              {filename || 'Uploaded PDF'}
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => blobUrl && window.open(blobUrl, '_blank')}
              disabled={!blobUrl}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              title="Open in new tab"
            >
              <ExternalLink className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden bg-gray-100 dark:bg-gray-900">
          {loading && (
            <div className="flex items-center justify-center h-full">
              <Loader className="w-8 h-8 text-blue-500 animate-spin" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading PDF...</span>
            </div>
          )}
          {error && (
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <div className="text-red-500 text-lg mb-2">Failed to load PDF</div>
                <div className="text-gray-500">{error}</div>
              </div>
            </div>
          )}
          {blobUrl && !loading && !error && (
            <iframe src={blobUrl} className="w-full h-full border-0" title="PDF Viewer" />
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFViewerModal;
